/**
 * registry/engine.ts — `createEngine(registry)` (tech/21 §3, §6).
 *
 * Реестр инъектируется в движок — ядро не знает конкретных карт (20 §2).
 * Engine объединяет публичные initState/validate/apply/serialize и внутренний
 * механизм диспетчеризации хуков из реестра:
 *  - dispatchEvent — прогон синтетической эмиссии события через хуки; может
 *    вернуть status:'awaiting_choice' + pendingChoice, если хук-генератор
 *    приостановился на ctx.choose (R2/D15);
 *  - resolve — возобновление приостановленного шага по choiceId + selection.
 *
 * «Приостановленный шаг» (живой генератор + позиция дренажа) ТРАНЗИЕНТЕН: движок
 * держит его в приватной Map choiceId→Suspension (в памяти, НЕ в GameState — там
 * только сериализуемый pendingChoice). Реплей воссоздаёт его детерминированно.
 */
import { deserialize as deserializeState, serialize as serializeState } from '../serialize/serialize.js';
import { redactEvents as redactEventStream, redactFor } from '../serialize/redact.js';
import { applyEvent } from '../events/reducer.js';
import { validate } from '../actions/validate.js';
import { RoundResolutionWorkflow } from '../actions/roundWorkflow.js';
import { initState, } from '../state/initState.js';
import { dispatchSyntheticEvent } from './dispatch.js';
import { resumeResolution, startResolutionWorkflow, } from './resolution.js';
import { legacyPlanCardSelections, placementCellFromSelections, validateCommittedPlanningSelections, } from '../targeting/planning.js';
/** Создать движок с инъектированным реестром карт. */
export function createEngine(registry) {
    // Транзиентный реестр приостановленных шагов (в памяти сервера, не в состоянии).
    const suspensions = new Map();
    const suspensionKey = (matchId, choiceId) => `${matchId}\u0000${choiceId}`;
    const remember = (result) => {
        if (result.status === 'awaiting_choice') {
            const key = suspensionKey(result.state.matchId, result.pendingChoice.choiceId);
            const existing = suspensions.get(key);
            if (existing !== undefined && existing !== result.suspension) {
                throw new Error(`engine: collision suspension для matchId "${result.state.matchId}" choiceId "${result.pendingChoice.choiceId}"`);
            }
            suspensions.set(key, result.suspension);
        }
        return result;
    };
    const resumeScoped = (matchId, choiceId, selection, expectedState) => {
        const key = suspensionKey(matchId, choiceId);
        const suspension = suspensions.get(key);
        if (suspension === undefined) {
            throw new Error(`engine.resolve: нет ожидающего выбора для matchId "${matchId}" choiceId "${choiceId}"`);
        }
        if (expectedState !== undefined && expectedState !== suspension.draft) {
            throw new Error(`engine.apply(Resolve): stale/wrong state lineage для matchId "${matchId}" choiceId "${choiceId}"`);
        }
        const next = resumeResolution(suspension, choiceId, selection);
        suspensions.delete(key);
        return remember(next);
    };
    const applyPlanningEvent = (state, event) => {
        const draft = structuredClone(state);
        applyEvent(draft, event);
        return { status: 'completed', state: draft, events: [event] };
    };
    const canExposeVar = (event, state) => {
        if (event.public !== true || state === undefined)
            return false;
        const target = event.target;
        let defId;
        switch (target.kind) {
            case 'unit':
                defId = state.instances[target.instanceId]?.defId;
                break;
            case 'effect':
                defId = state.effects.find((effect) => effect.id === target.effectId)?.defId;
                break;
            case 'attachment':
                defId = state.attachments.find((attachment) => attachment.attachmentId === target.attachmentId)?.defId;
                break;
        }
        return defId !== undefined && registry.get(defId)?.publicVarKeys?.includes(event.key) === true;
    };
    const approvedPublicVarEvents = (events, stateAtBatchStart) => {
        if (stateAtBatchStart === undefined)
            return new Set();
        const replay = structuredClone(stateAtBatchStart);
        const approved = new Set();
        try {
            for (const event of events) {
                if (event.type === 'VarChanged' && canExposeVar(event, replay))
                    approved.add(event);
                applyEvent(replay, event);
            }
        }
        catch {
            // A caller supplied an invalid/incomplete batch. It must never turn that
            // failure into a private-var disclosure; ordinary events still use the
            // generic conservative redactor below.
            return new Set();
        }
        return approved;
    };
    return {
        registry,
        serialize: serializeState,
        deserialize: deserializeState,
        redactFor,
        redactEvents(events, viewer, stateAtBatchStart) {
            const approved = approvedPublicVarEvents(events, stateAtBatchStart);
            return redactEventStream(events, viewer, {
                canExposeVar: (event) => approved.has(event),
            });
        },
        initState(params) {
            if (params.registry !== undefined && params.registry !== registry) {
                throw new Error('engine.initState: переданный registry не совпадает с registry движка');
            }
            return initState({
                seed: params.seed,
                players: params.players,
                registry,
                ...(params.matchId !== undefined ? { matchId: params.matchId } : {}),
            });
        },
        validate(state, action, by) {
            return validate(state, action, by, registry);
        },
        dispatchEvent(state, event) {
            return remember(dispatchSyntheticEvent(registry, state, event));
        },
        resolve(choiceId, selection) {
            const suffix = `\u0000${choiceId}`;
            const candidates = [...suspensions.entries()].filter(([key]) => key.endsWith(suffix));
            if (candidates.length !== 1) {
                throw new Error(candidates.length === 0
                    ? `engine.resolve: нет ожидающего выбора для choiceId "${choiceId}"`
                    : `engine.resolve: choiceId "${choiceId}" неоднозначен между матчами; используйте apply(Resolve)`);
            }
            const [, suspension] = candidates[0];
            return resumeScoped(suspension.draft.matchId, choiceId, selection, suspension.draft);
        },
        apply(state, action) {
            switch (action.kind) {
                case 'PlanCard': {
                    let planningState = state;
                    const planningEvents = [];
                    const existingPlayer = state.players[action.playerId];
                    if (existingPlayer?.staged.some((plan) => plan.instanceId === action.instanceId) === true) {
                        const unplanned = applyPlanningEvent(state, {
                            type: 'CardUnplanned', playerId: action.playerId, instanceId: action.instanceId,
                        });
                        planningState = unplanned.state;
                        planningEvents.push(...unplanned.events);
                    }
                    const instance = planningState.instances[action.instanceId];
                    const definition = instance === undefined ? undefined : registry.get(instance.defId);
                    if (definition === undefined)
                        throw new Error(`engine.apply(PlanCard): неизвестная карта "${action.instanceId}"`);
                    const player = planningState.players[action.playerId];
                    if (player === undefined)
                        throw new Error(`engine.apply(PlanCard): неизвестный игрок "${action.playerId}"`);
                    const submitted = legacyPlanCardSelections(action, definition);
                    const selections = validateCommittedPlanningSelections(planningState, registry, action.playerId, action.instanceId, definition, submitted).selections;
                    const plan = definition.type === 'SPELL'
                        ? {
                            kind: 'SPELL',
                            instanceId: action.instanceId,
                            planSeq: player.nextPlanSeq,
                            selections,
                        }
                        : definition.type === 'VIRUS'
                            ? {
                                kind: 'VIRUS',
                                instanceId: action.instanceId,
                                planSeq: player.nextPlanSeq,
                                selections,
                            }
                            : definition.type === 'REACTION'
                                ? {
                                    kind: 'REACTION',
                                    instanceId: action.instanceId,
                                    planSeq: player.nextPlanSeq,
                                    selections,
                                }
                                : {
                                    kind: 'FIELD',
                                    instanceId: action.instanceId,
                                    // Structural reveal index, deterministically derived from
                                    // the sole persisted placement selection below.
                                    cellId: placementCellFromSelections(selections),
                                    selections,
                                };
                    const planned = applyPlanningEvent(planningState, {
                        type: 'CardPlanned', playerId: action.playerId, plan,
                    });
                    return planningEvents.length === 0 ? planned : {
                        ...planned,
                        events: [...planningEvents, ...planned.events],
                    };
                }
                case 'UnplanCard':
                    return applyPlanningEvent(state, {
                        type: 'CardUnplanned', playerId: action.playerId, instanceId: action.instanceId,
                    });
                case 'PlaceUnit':
                    return applyPlanningEvent(state, {
                        type: 'CardPlanned',
                        playerId: action.playerId,
                        plan: {
                            kind: 'FIELD',
                            instanceId: action.instanceId,
                            cellId: action.cellId,
                            selections: [{
                                    stepId: 'placement',
                                    selection: [{ kind: 'cell', cellId: action.cellId }],
                                }],
                        },
                    });
                case 'Unplace':
                    return applyPlanningEvent(state, {
                        type: 'PlacementUnstaged',
                        playerId: action.playerId,
                        cellId: action.cellId,
                    });
                case 'Ready': {
                    const ready = applyPlanningEvent(state, {
                        type: 'PlayerReadyChanged',
                        playerId: action.playerId,
                        ready: true,
                    });
                    if (!Object.values(ready.state.players).every((player) => player.ready))
                        return ready;
                    const workflow = new RoundResolutionWorkflow(registry, (resources) => {
                        const energyMax = resources.energyMax + 1;
                        return { energy: energyMax, energyMax };
                    }, Object.values(ready.state.players).flatMap((player) => player.staged.map((placement) => placement.instanceId)));
                    return remember(startResolutionWorkflow(registry, ready.state, workflow, ready.events));
                }
                case 'Resolve': {
                    const pending = state.pendingChoice;
                    if (pending === undefined || pending.choiceId !== action.choiceId) {
                        throw new Error(`engine.apply(Resolve): choiceId "${action.choiceId}" не ожидается`);
                    }
                    if (pending.playerId !== action.playerId) {
                        throw new Error(`engine.apply(Resolve): выбор "${action.choiceId}" должен сделать "${pending.playerId}", не "${action.playerId}"`);
                    }
                    return resumeScoped(state.matchId, action.choiceId, action.selection, state);
                }
            }
        },
    };
}
//# sourceMappingURL=engine.js.map